import { HttpClient, HttpParams } from '@angular/common/http';
import { skipErrorToast } from 'src/app/core/http/error-toast.token';
import { Injectable } from '@angular/core';
import { delay, Observable, of, switchMap } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { environment } from 'src/environments/environment';
import {
  ICreateWorkflowRuleDTO,
  IEnumOption,
  IUpdateWorkflowRuleDTO,
  IWorkflowExecution,
  IWorkflowExecutionThread,
  WorkflowHistoryStatusFilter,
  IWorkflowRule,
  IWorkflowRuleSummary,
  IWorkflowSchema,
  IWorkflowTemplate,
  WorkflowRuleStatus,
} from '../models/workflows.models';
import {
  MOCK_WORKFLOW_ENUM_VALUES,
  MOCK_WORKFLOW_HISTORY,
  MOCK_WORKFLOW_RULES,
  MOCK_WORKFLOW_SCHEMA,
  MOCK_WORKFLOW_TEMPLATES,
} from '../mocks/workflows.mocks';

const MOCK_RULES: IWorkflowRuleSummary[] = MOCK_WORKFLOW_RULES;
const MOCK_HISTORY: IWorkflowExecution[] = MOCK_WORKFLOW_HISTORY;

// ─────────────────────────────────────────────────────────────────────────────

const BASE = `${environment.BASE_URL_API}workflow-rules`;

function mockDelay<T>(data: T): Observable<T> {
  return of(null).pipe(delay(300), switchMap(() => of(data)));
}

@Injectable({
  providedIn: 'root',
})
export class WorkflowsService {
  private readonly useMock = environment.useMockData ?? false;

  constructor(private http: HttpClient) {}

  // ── Schema ──────────────────────────────────────────────────────────────

  getSchema(): Observable<IWorkflowSchema> {
    if (this.useMock) {
      return mockDelay(this.filterDisabledSchema(MOCK_WORKFLOW_SCHEMA));
    }
    return this.http.get<any>(`${BASE}/schema`).pipe(
      switchMap((res) => of(res.object ?? res.data?.[0] ?? res))
    );
  }

  // ── Templates ───────────────────────────────────────────────────────────

  /** Predefined rule skeletons for the "start from template" entry point.
   *  Backend-owned like the schema, and already filtered there to templates
   *  whose trigger/actions/fields are all currently enabled. */
  getWorkflowTemplates(): Observable<IWorkflowTemplate[]> {
    if (this.useMock) {
      return mockDelay(MOCK_WORKFLOW_TEMPLATES);
    }
    return this.http.get<any>(`${BASE}/templates`).pipe(
      switchMap((res) => of(res.object?.templates ?? res.data?.[0]?.templates ?? []))
    );
  }

  getEnumValues(fieldKey: string): Observable<IEnumOption[]> {
    if (this.useMock) {
      return mockDelay(MOCK_WORKFLOW_ENUM_VALUES[fieldKey] ?? []);
    }
    const params = new HttpParams().set('field', fieldKey);
    return this.http.get<any>(`${BASE}/enum-values`, { params }).pipe(
      switchMap((res) => of(res.data ?? []))
    );
  }

  searchEnumValues(fieldKey: string, search: string): Observable<IEnumOption[]> {
    const normalizedSearch = search.trim().toLowerCase();

    if (this.useMock) {
      const options = MOCK_WORKFLOW_ENUM_VALUES[fieldKey] ?? [];
      const filteredOptions = normalizedSearch
        ? options.filter((option) => option.label.toLowerCase().includes(normalizedSearch))
        : options;

      return mockDelay(filteredOptions);
    }

    let params = new HttpParams().set('field', fieldKey);
    if (normalizedSearch) {
      params = params.set('search', normalizedSearch);
    }

    return this.http.get<any>(`${BASE}/enum-values`, { params }).pipe(
      switchMap((res) => of(res.data ?? []))
    );
  }

  // ── List ────────────────────────────────────────────────────────────────

  getWorkflowRules(): Observable<APIResponse<IWorkflowRuleSummary>> {
    if (this.useMock) {
      return mockDelay<APIResponse<IWorkflowRuleSummary>>({
        message: 'ok',
        data: MOCK_RULES,
        metadata: { page: 1, limit: 25, pages: 1, count: MOCK_RULES.length },
      });
    }
    return this.http.get<APIResponse<IWorkflowRuleSummary>>(BASE);
  }

  // ── Get single rule ─────────────────────────────────────────────────────

  getWorkflowRule(id: string): Observable<IWorkflowRule> {
    if (this.useMock) {
      const summary = MOCK_RULES.find((r) => r.id === id);
      const mock: IWorkflowRule = {
        id: summary?.id ?? id,
        name: summary?.name ?? 'Mock Rule',
        description: summary?.description ?? null,
        status: summary?.status ?? 'draft',
        trigger: { type: summary?.triggerType ?? 'new_email_received', params: {} },
        conditionGroups: [
          {
            id: 'cg-1',
            match: 'all',
            conditions: [
              { id: 'c-1', field: 'email.subject', operator: 'contains', value: 'PO' },
            ],
          },
        ],
        actions: [
          { id: 'a-1', type: 'assign_user', params: { userId: 1 }, order: 0 },
        ],
        stopProcessing: summary?.stopProcessing ?? false,
        createdAt: summary?.createdAt ?? new Date().toISOString(),
        updatedAt: summary?.updatedAt ?? new Date().toISOString(),
        timesRun: summary?.timesRun ?? 0,
        lastRunAt: summary?.lastRunAt ?? null,
      };
      return mockDelay(mock);
    }
    return this.http.get<any>(`${BASE}/${id}`).pipe(
      switchMap((res) => of(res.object ?? res.data?.[0] ?? res))
    );
  }

  // ── Create ──────────────────────────────────────────────────────────────

  createWorkflowRule(dto: ICreateWorkflowRuleDTO): Observable<IWorkflowRule> {
    if (this.useMock) {
      const created: IWorkflowRule = {
        id: `mock-${Date.now()}`,
        name: dto.name,
        description: dto.description,
        status: 'draft',
        trigger: dto.trigger,
        conditionGroups: dto.conditionGroups,
        actions: dto.actions,
        stopProcessing: dto.stopProcessing,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        timesRun: 0,
        lastRunAt: null,
      };
      return mockDelay(created);
    }
    return this.http.post<any>(BASE, dto).pipe(
      switchMap((res) => of(res.object ?? res.data?.[0] ?? res))
    );
  }

  // ── Update ──────────────────────────────────────────────────────────────

  updateWorkflowRule(id: string, dto: IUpdateWorkflowRuleDTO): Observable<IWorkflowRule> {
    if (this.useMock) {
      return this.getWorkflowRule(id).pipe(
        switchMap((existing) =>
          of({ ...existing, ...dto, updatedAt: new Date().toISOString() } as IWorkflowRule)
        )
      );
    }
    return this.http.put<any>(`${BASE}/${id}`, dto).pipe(
      switchMap((res) => of(res.object ?? res.data?.[0] ?? res))
    );
  }

  // ── Delete ──────────────────────────────────────────────────────────────

  deleteWorkflowRule(id: string): Observable<void> {
    if (this.useMock) {
      return mockDelay(undefined as void);
    }
    return this.http.delete<void>(`${BASE}/${id}`);
  }

  // ── Toggle status ───────────────────────────────────────────────────────

  /**
   * @param silent When `true`, opt out of the global error toast so the caller
   *   can surface its own contextual message (avoids the double-toast problem).
   *   Defaults to `false` so the standalone rules-list toggle keeps the global
   *   toast — only the save flow, which shows its own WARNING, passes `true`.
   */
  toggleWorkflowRuleStatus(
    id: string,
    status: WorkflowRuleStatus,
    silent = false
  ): Observable<IWorkflowRule> {
    if (this.useMock) {
      return this.getWorkflowRule(id).pipe(
        switchMap((existing) =>
          of({ ...existing, status, updatedAt: new Date().toISOString() } as IWorkflowRule)
        )
      );
    }
    const options = silent ? { context: skipErrorToast() } : {};
    return this.http.patch<any>(`${BASE}/${id}/status`, { status }, options).pipe(
      switchMap((res) => of(res.object ?? res.data?.[0] ?? res))
    );
  }

  // ── History ─────────────────────────────────────────────────────────────

  getRuleHistory(
    ruleId: string,
    page = 1,
    limit = 25,
    search = '',
    status: WorkflowHistoryStatusFilter = ''
  ): Observable<APIResponse<IWorkflowExecution>> {
    if (this.useMock) {
      const filtered = this.filterHistoryEntries(MOCK_HISTORY, search, status).filter(
        (entry) => entry.ruleId === ruleId
      );
      const start = (page - 1) * limit;
      const data = filtered.slice(start, start + limit);
      return mockDelay<APIResponse<IWorkflowExecution>>({
        message: 'ok',
        data,
        metadata: {
          page,
          limit,
          pages: Math.ceil(filtered.length / limit),
          count: filtered.length,
        },
      });
    }
    const params = this.buildHistoryParams(page, limit, search, status);
    return this.http.get<APIResponse<IWorkflowExecution>>(`${BASE}/${ruleId}/history`, { params }).pipe(
      switchMap((response) => of({
        ...response,
        data: (response.data ?? []).map((execution: any) => ({
          ...execution,
          thread: this.normalizeHistoryThread(execution),
        })),
      }))
    );
  }

  getGlobalHistory(
    page = 1,
    limit = 25,
    search = '',
    status: WorkflowHistoryStatusFilter = ''
  ): Observable<APIResponse<IWorkflowExecution>> {
    if (this.useMock) {
      const filtered = this.filterHistoryEntries(MOCK_HISTORY, search, status);
      const start = (page - 1) * limit;
      const data = filtered.slice(start, start + limit);
      return mockDelay<APIResponse<IWorkflowExecution>>({
        message: 'ok',
        data,
        metadata: {
          page,
          limit,
          pages: Math.ceil(filtered.length / limit),
          count: filtered.length,
        },
      });
    }
    const params = this.buildHistoryParams(page, limit, search, status);
    return this.http.get<APIResponse<IWorkflowExecution>>(`${BASE}/history`, { params }).pipe(
      switchMap((response) => of({
        ...response,
        data: (response.data ?? []).map((execution: any) => ({
          ...execution,
          thread: this.normalizeHistoryThread(execution),
        })),
      }))
    );
  }

  // ── AI Generate ─────────────────────────────────────────────────────────

  generateRuleWithAi(prompt: string): Observable<IWorkflowRule> {
    return this.http.post<any>(`${BASE}/generate`, { prompt }).pipe(
      switchMap((res) => of(res.object ?? res.data?.[0] ?? res))
    );
  }

  private filterDisabledSchema(schema: IWorkflowSchema): IWorkflowSchema {
    return {
      ...schema,
      // Planned for a future version: keep disabled schema entries hidden for now.
      triggers: (schema.triggers ?? []).filter((trigger) => !trigger.disabled),
      conditionFields: (schema.conditionFields ?? []).filter((field) => !field.disabled),
      actions: (schema.actions ?? []).filter((action) => !action.disabled),
    };
  }

  private normalizeHistoryThread(execution: any): IWorkflowExecutionThread | null {
    if (execution?.thread && execution.thread.id) {
      return {
        id: execution.thread.id,
        subject: execution.thread.subject ?? '',
      };
    }

    // Backward compatibility while backend payload is rolling out.
    if (execution?.threadId) {
      return {
        id: execution.threadId,
        subject: '',
      };
    }

    return null;
  }

  private buildHistoryParams(
    page: number,
    limit: number,
    search: string,
    status: WorkflowHistoryStatusFilter
  ): HttpParams {
    let params = new HttpParams().set('page', page).set('limit', limit);
    const normalizedSearch = search.trim();

    if (normalizedSearch) {
      params = params.set('search', normalizedSearch);
    }

    if (status) {
      params = params.set('status', status);
    }

    return params;
  }

  private filterHistoryEntries(
    entries: IWorkflowExecution[],
    search: string,
    status: WorkflowHistoryStatusFilter
  ): IWorkflowExecution[] {
    const normalizedSearch = search.trim().toLowerCase();

    return entries.filter((entry) => {
      const matchesSearch = !normalizedSearch
        || entry.ruleName.toLowerCase().includes(normalizedSearch)
        || entry.thread?.subject?.toLowerCase().includes(normalizedSearch);
      const matchesStatus = !status || entry.status === status;

      return matchesSearch && matchesStatus;
    });
  }
}
